<template>
  <div class="flex flex-row flex-wrap justify-center gap-10 items-center min-h-screen pb-20">
    <div class="flex flex-col items-center gap-4">
      <div class="w-full max-w-3xl rounded-lg border border-gray-200 bg-white p-5 shadow-sm">
        <h2 class="mb-4 text-lg font-semibold text-gray-700">{{ $t('export.filterTitle') }}</h2>
        <div class="grid gap-4 sm:grid-cols-2">
          <label class="flex flex-col gap-1 text-sm text-gray-700">
            <span>{{ $t('export.dateFrom') }}</span>
            <input
              v-model="dateFrom"
              type="date"
              :max="dateTo || undefined"
              class="rounded-md border border-gray-300 px-3 py-2"
              :disabled="isLoading"
            >
          </label>
          <label class="flex flex-col gap-1 text-sm text-gray-700">
            <span>{{ $t('export.dateTo') }}</span>
            <input
              v-model="dateTo"
              type="date"
              :min="dateFrom || undefined"
              class="rounded-md border border-gray-300 px-3 py-2"
              :disabled="isLoading"
            >
          </label>
        </div>

        <fieldset class="mt-5">
          <legend class="mb-2 text-sm font-medium text-gray-700">{{ $t('export.statusLabel') }}</legend>
          <div class="flex flex-wrap gap-x-5 gap-y-2">
            <label
              v-for="option in statusOptions"
              :key="option.value"
              class="flex items-center gap-2 text-sm text-gray-700"
            >
              <input
                v-model="selectedStatuses"
                type="checkbox"
                :value="option.value"
                class="h-4 w-4 accent-cyan-500"
                :disabled="isLoading"
              >
              <span>{{ $t(option.labelKey) }}</span>
            </label>
          </div>
        </fieldset>

        <label v-if="canFilterByManager" class="mt-5 flex flex-col gap-1 text-sm text-gray-700">
          <span>{{ $t('export.managerLabel') }}</span>
          <select
            v-model="gestorAdsId"
            class="rounded-md border border-gray-300 bg-white px-3 py-2"
            :disabled="isLoading || filtersLoading"
          >
            <option value="">{{ $t('export.allManagers') }}</option>
            <option v-for="manager in gestoresAds" :key="manager.id" :value="manager.id">
              {{ manager.name }}
            </option>
          </select>
        </label>

        <p class="mt-3 rounded-md bg-cyan-50 px-3 py-2 text-xs text-cyan-800">
          {{ $t(canFilterByManager ? 'export.globalScopeHelp' : 'export.ownScopeHelp') }}
        </p>
        <p class="mt-3 text-xs text-gray-500">{{ $t('export.workflowHelp') }}</p>
        <div class="mt-3 flex flex-wrap items-center justify-between gap-3">
          <p class="text-xs text-gray-500">{{ $t('export.dateHelp') }}</p>
          <button
            type="button"
            class="text-sm text-cyan-600 underline disabled:opacity-50"
            :disabled="isLoading"
            @click="resetFilters"
          >
            {{ $t('export.resetFilters') }}
          </button>
        </div>
      </div>
      <RouterLink to="#" @click.prevent="exportFile" class="flex items-center gap-2">
        <buttonSquare :title=" $t('export.requests') " :disabled="isLoading">
          <img src="@/assets/img/home.svg" alt="" class="w-20" >
        </buttonSquare>
      </RouterLink>
      <div v-if="isLoading" class="flex items-center gap-2">
        <div class="animate-spin rounded-full h-6 w-6 border-b-2 border-gray-900"></div>
        <span>{{ $t('export.loading') }}</span>
      </div>
      <p v-if="errorMessage" class="max-w-md text-center text-red-600" role="alert">
        {{ errorMessage }}
      </p>
      <p
        v-if="successMessage"
        class="max-w-md rounded-md border border-green-200 bg-green-50 px-4 py-3 text-center text-green-700"
        role="status"
      >
        {{ successMessage }}
      </p>
    </div>

    <div
      v-if="isLoading"
      class="fixed inset-0 z-[100] flex items-center justify-center bg-black/30 px-4"
      role="status"
      aria-live="polite"
      aria-busy="true"
    >
      <div class="w-full max-w-md rounded-xl bg-white p-6 text-center shadow-2xl">
        <div class="mx-auto h-10 w-10 animate-spin rounded-full border-4 border-cyan-100 border-t-cyan-500"></div>
        <h2 class="mt-4 text-lg font-semibold text-gray-800">{{ stageMessage }}</h2>
        <p v-if="rowsTotal > 0" class="mt-2 text-sm text-gray-600">
          {{ $t('export.progressRows', { processed: rowsProcessed, total: rowsTotal }) }}
        </p>
        <div v-if="rowsTotal > 0" class="mt-3 h-2 overflow-hidden rounded-full bg-gray-200">
          <div
            class="h-full rounded-full bg-cyan-500 transition-all duration-300"
            :style="{ width: `${progressPercent}%` }"
          ></div>
        </div>
        <p v-if="etaSeconds" class="mt-2 text-xs text-gray-500">
          {{ $t('export.estimatedTime', { seconds: etaSeconds }) }}
        </p>
        <p class="mt-4 text-xs text-gray-500">{{ $t('export.keepWindowOpen') }}</p>
      </div>
    </div>
  </div>
</template>

<script setup>
import { computed, onBeforeUnmount, onMounted, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import axios from 'axios';
import buttonSquare from '@/components/button/button-square.vue';
import { defaultExportRange, exportRangeError } from '@/services/exportDateRange';

const { t } = useI18n();
const isLoading = ref(false);
const errorMessage = ref('');
const successMessage = ref('');
const exportStage = ref('preparing');
const rowsTotal = ref(0);
const rowsProcessed = ref(0);
const etaSeconds = ref(null);
// Rango obligatorio de un año como máximo; arranca con los últimos doce meses (ADS-0186).
const dateFrom = ref(defaultExportRange().from);
const dateTo = ref(defaultExportRange().to);
const statusOptions = [
  { value: 'created', labelKey: 'export.statuses.created' },
  { value: 'review', labelKey: 'export.statuses.review' },
  { value: 'pending', labelKey: 'export.statuses.pending' },
  { value: 'results', labelKey: 'export.statuses.results' },
  { value: 'cancel', labelKey: 'export.statuses.cancel' },
];
const defaultStatuses = ['created', 'review', 'pending'];
const selectedStatuses = ref([...defaultStatuses]);
const gestorAdsId = ref('');
const gestoresAds = ref([]);
const filtersLoading = ref(false);
const canFilterByManager = ref(false);
let polling;
let successTimer;

const stageMessage = computed(() => t(`export.stages.${exportStage.value}`));
const progressPercent = computed(() => {
  if (rowsTotal.value <= 0) return 0;
  return Math.min(100, Math.round((rowsProcessed.value / rowsTotal.value) * 100));
});

const resetFilters = () => {
  const range = defaultExportRange();
  dateFrom.value = range.from;
  dateTo.value = range.to;
  selectedStatuses.value = [...defaultStatuses];
  gestorAdsId.value = '';
};

const fetchFilters = async () => {
  filtersLoading.value = true;
  try {
    const response = await axios.get(
      `${import.meta.env.VITE_API_URL}/request/user/export/incidents/filters`,
      {
        headers: {
          Accept: 'application/json',
          Authorization: `Bearer ${localStorage.getItem('token')}`,
        },
      }
    );
    gestoresAds.value = response.data?.value?.gestores_ads ?? [];
    canFilterByManager.value = response.data?.value?.can_filter_by_manager === true;
    if (!canFilterByManager.value) gestorAdsId.value = '';
  } catch (error) {
    console.error(t('export.errors.filterLoadFailed'), error);
  } finally {
    filtersLoading.value = false;
  }
};

const stopPolling = () => {
  if (polling) {
    clearInterval(polling);
    polling = undefined;
  }
};

const exportFile = async () => {
  if (isLoading.value) return;

  isLoading.value = true;
  errorMessage.value = '';
  successMessage.value = '';
  exportStage.value = 'preparing';
  rowsTotal.value = 0;
  rowsProcessed.value = 0;
  etaSeconds.value = null;
  if (successTimer) clearTimeout(successTimer);
  stopPolling();

  const rangeProblem = exportRangeError(dateFrom.value, dateTo.value);
  if (rangeProblem) {
    handleExportError(new Error(t(rangeProblem === 'order' ? 'export.errors.invalidDateRange' : `export.range.${rangeProblem}`)));
    return;
  }

  if (selectedStatuses.value.length === 0) {
    handleExportError(new Error(t('export.errors.noStatuses')));
    return;
  }

  try {
    const exportResponse = await axios.get(
      `${import.meta.env.VITE_API_URL}/request/user/export/incidents`,
      {
        headers: {
          Accept: "application/json",
          Authorization: `Bearer ${localStorage.getItem("token")}`,
        },
        params: {
          file_name: t('export.filename'),
          file_type: "xlsx",
          ...(dateFrom.value ? { date_from: dateFrom.value } : {}),
          ...(dateTo.value ? { date_to: dateTo.value } : {}),
          statuses: selectedStatuses.value,
          ...(canFilterByManager.value && gestorAdsId.value ? { gestor_ads_id: gestorAdsId.value } : {}),
        },
      }
    );

    const jobId = exportResponse.data.value.job_id;
    if (!jobId) throw new Error(t('export.errors.noJobId'));
    exportStage.value = 'processing';

    const pollInterval = 2000;
    const checkStatus = async () => {
      try {
        const statusResponse = await axios.get(
          `${import.meta.env.VITE_API_URL}/export/status`,
          {
            headers: {
              Accept: "application/json",
              Authorization: `Bearer ${localStorage.getItem("token")}`,
            },
            params: { job_id: jobId },
          }
        );

        const job = statusResponse.data.value;
        rowsTotal.value = Number(job.rows_total) || 0;
        rowsProcessed.value = Number(job.rows_processed) || 0;
        etaSeconds.value = Number(job.eta_seconds) || null;

        if (job.status === "finished") {
          stopPolling();
          rowsProcessed.value = rowsTotal.value;
          exportStage.value = 'downloading';
          await handleSuccessfulExport(statusResponse);
        } else if (job.status === "failed") {
          stopPolling();
          throw new Error(t('export.errors.failed'));
        }
      } catch (err) {
        stopPolling();
        handleExportError(err);
      }
    };

    polling = setInterval(checkStatus, pollInterval);
    checkStatus();
  } catch (error) {
    handleExportError(error);
  }
};

const handleSuccessfulExport = async (response) => {
  const filePath = response.data.value.file_path;
  if (!filePath) throw new Error(t('export.errors.noFilePath'));

  try {
    const downloadResponse = await axios.get(
      `${import.meta.env.VITE_API_URL}/export/download`,
      {
        headers: {
          Accept: "application/json",
          Authorization: `Bearer ${localStorage.getItem("token")}`,
        },
        params: { path: filePath },
        responseType: "blob",
      }
    );

    const blob = new Blob([downloadResponse.data], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" });
    const link = document.createElement("a");
    link.href = window.URL.createObjectURL(blob);
    link.download = `${t('export.filename')}.xlsx`;
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(link.href);
    successMessage.value = t('export.downloadStarted');
    successTimer = setTimeout(() => {
      successMessage.value = '';
    }, 6000);
  } catch {
    throw new Error(t('export.errors.downloadFailed'));
  } finally {
    isLoading.value = false;
  }
};

const handleExportError = (error) => {
  stopPolling();
  console.error(t('export.errors.generic'), error);
  errorMessage.value = error?.response?.data?.message || error?.message || t('export.errors.generic');
  isLoading.value = false;
};

onMounted(fetchFilters);
onBeforeUnmount(() => {
  stopPolling();
  if (successTimer) clearTimeout(successTimer);
});
</script>

<style scoped>
/* Estilos existentes se mantienen igual */
</style>
